//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'
import * as moment from 'moment'

//#endregion

//#region Helper Imports

import { DataProviderRequest, DataProviderRequestError } from '../../data-provider';
import { CommonHelper, ConvertHelper, LocalizationHelper, LogHelper, MapHelper, ToastHelper, TypeHelper, DataTypeGroupComponent, ConfigHelper, Dictionary, KeyType } from '../../helpers'

//#endregion

//#region Model Imports

import { OpData, OpDevice, OpLocation, OpMeter, OpView, OpViewColumn, OpDataTypeInGroup, OpLocationType, OpIssue, OpIssueType, OpActivity } from '../../models/core'
import { GetViewColumnOptions } from '../../models/core/op-view'
import { Enums, Activity } from "../../models/common"
import { ObjectCustomString } from "../../models/util"
import { ColumnInfo, OpValidateInfo } from '../../models/custom'
import { OpProblemClass } from '../../models/dw';

import { IOpObject, IReferenceType } from '../../models/util/interfaces'

//#endregion

//#region Service Imports

import { SYSTEM_SERVICE_NAME, SystemService } from '../system';
import { REFERENCE_TYPE_SERVICE_NAME, ReferenceTypeService } from '../reference-type';
import { USER_SESSION_SERVICE_NAME, UserSessionService } from '../user-session'
import { GroupingLocationService, GROUPING_LOCATION_SERVICE_NAME } from '../grouping-locations';
import { DictionaryKeyType, KeyValuePair } from '../../helpers/dictionary';

//#endregion

import { ColumnModel, DeviceMeterModel, FieldNameValueModel, FormValuesModel, LocationFormValuesModel } from "../../modules/api/api.models";
import { CreateEditInitParams, BulkEditInitParams } from '../../modules/common/create-edit-popup/create-edit-popup.controller'
import { DetailsOptions } from "../../modules/common/details-popup/details-popup.directive";
import { GridService, GRID_SERVICE_NAME } from '../grid'
import { MapService, MAP_SERVICE_NAME } from '../../modules/common/map2';

// #region Consts

export const LOCATION_SERVICE_NAME: string = 'LocationService'
export const LOCATION_SERVICE_PROVIDER_NAME: string = 'LocationServiceProvider'

// #endregion

type GetLocationTypeOptions = {
    locationView?: OpView
}

export class LocationService {

    // #region Consts

    public static get GRID_NAME(): string { return 'LocationsGrid' }
    public static get TREE_NAME_EXTENDED_PARAMETERS(): string { return 'LocationsDetails' }

    public static get LOG_PREFIX(): string { return '[SIMAX.Module.Location]' }
    public static get REFERENCE_TYPE(): Enums.ReferenceType { return Enums.ReferenceType.IdLocation }
    public static get SIMAX_MODULE_NAME(): Enums.SimaxModuleName { return Enums.SimaxModuleName.Location }
    public static get FILE_NAME_PREFIX(): string { return 'locations' }

    public static get TreeKeyFieldName() { return 'idTreeItem' }
    public static get TreeParentFieldName() { return 'idParentTreeItem' }

    // #endregion

    // #region Ctor

    constructor(
        private readonly userSessionService: UserSessionService,
        private readonly gridService: GridService,
        private readonly referenceTypeService: ReferenceTypeService,
        private readonly systemService: SystemService,
        private readonly mapService: MapService,
        private readonly groupingLocationService: GroupingLocationService
    ) {
    }

    // #endregion

    // #region CreateEdit

    public openCreate(templateId: number = null): void {
        this.getCreateEditOptions(
            templateId || 0,
            undefined,
            undefined,
            !!templateId,
            this.userSessionService.getLoggedOperator().idDistributor
        )
            .then((createEditOptions: CreateEditInitParams) => {
                this.systemService.openCreateEdit(createEditOptions)
            })
    }

    public openEdit(idLocation: number, locationIdDistributor: number): void {
        this.getCreateEditOptions(idLocation, undefined, Enums.SimaxCreateEditSelectedTabType.BaseParameters, false, locationIdDistributor)
            .then((createEditOptions: CreateEditInitParams) => {
                this.systemService.openCreateEdit(createEditOptions)
            })
    }

    public openCopy(idLocation: number, locationIdDistributor: number): void {
        this.getCreateEditOptions(idLocation, undefined, Enums.SimaxCreateEditSelectedTabType.BaseParameters, true, locationIdDistributor)
            .then((createEditOptions: CreateEditInitParams) => {
                this.systemService.openCreateEdit(createEditOptions)
            })
    }

    public openEditByLocation(location: OpLocation): void {
        if (location != null) {
            this.getCreateEditOptions(location.idLocation, location)
                .then((createEditOptions: CreateEditInitParams) => {
                    this.systemService.openCreateEdit(createEditOptions)
                })
        }
    }

    public openExtendedParametersEditByLocation(location: OpLocation): void {
        if (location != null) {
            this.getCreateEditOptions(location.idLocation, location, Enums.SimaxCreateEditSelectedTabType.ExtendedParameters)
                .then((createEditOptions: CreateEditInitParams) => {
                    this.systemService.openCreateEdit(createEditOptions)
                })
        }
    }

    public getCreateEditOptions(
        referenceObjectId: number = 0,
        referenceObject: OpLocation = null,
        selectedTabType: Enums.SimaxCreateEditSelectedTabType = Enums.SimaxCreateEditSelectedTabType.BaseParameters,
        isCopy: boolean = false,
        dataTypeGroupReferenceValue: number = null
    ): JQueryPromise<CreateEditInitParams> {
        return this.getAvailableLocationTypes(Enums.ReferenceType.IdLocationType)
            .then((locationTypes: Dictionary<OpLocationType[]>) => {
                return {
                    createEditSelectedTabType: selectedTabType,
                    groupReferenceType: Enums.ReferenceType.IdDistributor,
                    groupReferenceValue: referenceObject != null ? referenceObject.idDistributor : dataTypeGroupReferenceValue != null ? dataTypeGroupReferenceValue : null,
                    idReferenceType: LocationService.REFERENCE_TYPE,
                    moduleName: LocationService.SIMAX_MODULE_NAME,
                    referenceObject: referenceObject,
                    referenceObjectId: referenceObjectId || 0,
                    sections: null,
                    useBusinessObjects: true,
                    dedicatedForm: (
                        this.userSessionService.getLoggedOperator().profile != null &&
                        this.userSessionService.getLoggedOperator().profile.parameters != null)
                        ? this.userSessionService.getLoggedOperator().profile.parameters.getValue(Enums.DataType.PROFILE_LOCATION_EDIT_FORM)
                        : null,
                    isCopy: isCopy,

                    customDataSources: locationTypes
                }
            })
    }

    //#endregion

    // #region Delete

    public deleteLocation(idLocation: number): JQueryPromise<void> {
        const deferred: JQueryDeferred<void> = $.Deferred<void>()
        const toDelete: OpLocation = new OpLocation(null)

        toDelete.idLocation = idLocation

        this.userSessionService
            .getDataProvider()
            .Location
            .deleteLocation(toDelete)
            .then(() => {
                ToastHelper.showSuccess(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETED, LocationService.SIMAX_MODULE_NAME))
                deferred.resolve()
            })
            .fail((error: any) => {
                const errorMsg: string = ConvertHelper.getRequestErrorMessage(error)
                ToastHelper.showError(errorMsg || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))

                deferred.reject(errorMsg)
            })

        return deferred.promise()
    }

    // #endregion

    // #region Location Groups

    public getLocationGroupsGridColumns(): ColumnModel[] {
        const idColumn: ColumnModel = new ColumnModel()
        idColumn.idName = 'id'
        idColumn.name = 'id'
        idColumn.hidden = true
        idColumn.type = Enums.DataTypeClass.Integer

        const nameColumn: ColumnModel = new ColumnModel()
        nameColumn.idName = 'name'
        nameColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.AssignedGroups)
        nameColumn.type = Enums.DataTypeClass.Text
        nameColumn.selectedFilterOperation = 'contains'
        nameColumn.calculateCellValue = (data: any) => {
            return data == null
                ? TypeHelper.emptyString
                : this.gridService.getSourceObjectFromGridObject(data, LocationService.REFERENCE_TYPE).toString()
        }

        return [idColumn, nameColumn]
    }

    // #endregion

    // #region Location Type

    public getLocationType(location: OpLocation, options?: GetLocationTypeOptions): JQueryPromise<Enums.LocationType> {
        const locationTypeDeferred: JQueryDeferred<Enums.LocationType> = $.Deferred<Enums.LocationType>()

        if (location != null) {
            let locationType: Enums.LocationType = null

            if (location.locationType != null) {
                locationType = location.idLocationType
            }

            if (locationType == null && location.dataList != null) {
                locationType = location.dataList.getValue(Enums.DataType.HELPER_ID_LOCATION_TYPE)
            }

            if (locationType == null && location.dynamicValues != null) {
                return this.getLocationTypeFromDynamicValues(location, options != null ? options.locationView : null)
            }
            else {
                locationTypeDeferred.resolve(locationType)
            }
        }

        return locationTypeDeferred.promise()
    }

    private getLocationTypeFromDynamicValues(location: OpLocation, view: OpView): JQueryPromise<Enums.LocationType> {
        let locationViewDeferred: JQueryDeferred<OpView> = $.Deferred<OpView>()

        if (view == null) {
            this.getLocationView()
                .then((locationView: OpView) => {
                    locationViewDeferred.resolve(locationView)
                })
        }
        else {
            locationViewDeferred.resolve(view)
        }

        return locationViewDeferred.then((locationView: OpView) => {
            const locationTypeViewColumn: OpViewColumn = this.getLocationTypeColumn(locationView)
            let idLocationType: Enums.LocationType

            if (locationTypeViewColumn != null) {
                _.forEach(location.dynamicValues, dv => {
                    if (OpViewColumn.getColumnIdDataType(dv.Key) === Enums.DataType.HELPER_ID_LOCATION_TYPE && dv.Value != null) {
                        idLocationType = dv.Value
                    }
                })
            }

            if (_.isNumber(idLocationType)) {
                return idLocationType
            }

            if (_.isString(idLocationType)) {
                return this.referenceTypeService
                    .getDictionaryReferenceObjectList(Enums.ReferenceType.IdLocationType)
                    .then((locationTypes: OpLocationType[]) => {
                        const locationType: OpLocationType = _.find(locationTypes, (locationType: OpLocationType) => {
                            return locationType.toString().toLowerCase() === idLocationType.toString().toLowerCase()
                        })

                        return locationType != null ? locationType.idLocationType : null
                    })
            }

            return null
        })
    }

    private getLocationView(): JQueryPromise<OpView> {
        return this.userSessionService
            .getDataProvider().View
            .getActiveView(Enums.ReferenceType.IdLocation)
    }

    private getLocationTypeColumn(locationView: OpView): OpViewColumn {
        if (locationView != null) {
            return locationView.getViewColumn(new GetViewColumnOptions(Enums.DataType.HELPER_ID_LOCATION_TYPE))
        }

        return null
    }

    private getAvailableLocationTypes(referenceId: number): JQueryPromise<Dictionary<OpLocationType[]>> {
        return $.when(this.userSessionService
            .getDataProvider()
            .System
            .getDictionaries([referenceId]),

            this.groupingLocationService
                .getAvailableGroupingLocationTypes(referenceId)
        )
            .then((referenceDictionary: Dictionary<Dictionary<OpLocationType>>, groupingLocationTypesDict: Dictionary<OpLocationType[]>) => {
                const referenceLocationTypes: Dictionary<OpLocationType> = referenceDictionary.tryGetValue(Enums.ReferenceType.IdLocationType)
                const filteredLocationTypes: Dictionary<OpLocationType> = new Dictionary<OpLocationType>([], KeyType.Number)
                let groupingLocationTypes: OpLocationType[] = []

                if (groupingLocationTypesDict != null) {
                    _.forEach(groupingLocationTypesDict.toArray(), (pair: KeyValuePair<OpLocationType[]>) => groupingLocationTypes.push(...pair.value))
                }

                const groupingLoctionTypesIds: number[] = _.map(groupingLocationTypes, (locType: OpLocationType) => locType.getReferenceKey())

                referenceLocationTypes.forEach((key: KeyType.Number, value: OpLocationType, index: number) => {
                    if (groupingLoctionTypesIds.indexOf(+key) == -1
                        && +key !== Enums.LocationType.DepositoryLocation) {
                        filteredLocationTypes.addOrUpdate(key, value)
                    }
                })

                let hasAllPermission: boolean = false
                const referenceValues: number[] = _.filter(this.userSessionService
                    .getLoggedOperator()
                    .activities, (activity: OpActivity) => { return (activity.idActivity === Activity.ALLOWED_LOCATION_TYPE && activity.deny === false) })
                    .map((activity: OpActivity) => {
                        if (activity.referenceValue == null) {
                            hasAllPermission = true
                        }
                        return activity.referenceValue
                    })

                if (hasAllPermission) {
                    return new Dictionary([{ key: Enums.ReferenceType.IdLocationType, value: filteredLocationTypes.values() }], KeyType.Number)
                } else {
                    const locationTypes: OpLocationType[] = filteredLocationTypes.values()

                    if (_.size(referenceValues) === 0) {
                        return new Dictionary([{ key: Enums.ReferenceType.IdLocationType, value: locationTypes }], KeyType.Number)
                    } else {
                        const filteredTypes: OpLocationType[] = []
                        referenceValues.forEach((value: number) => {
                            locationTypes.forEach((item: OpLocationType) => {
                                if (value === item.idLocationType) {
                                    filteredTypes.push(item)
                                }
                            })
                        })
                        return new Dictionary([{ key: Enums.ReferenceType.IdLocationType, value: filteredTypes }], KeyType.Number)
                    }
                }
            })
    }

    // #endregion

    // #region Main Grid

    public getMainGridColumns(): JQueryPromise<ColumnModel[]> {
        const groupReferenceType: any = {
            groupReferenceType: Enums.ReferenceType.IdDistributor,
            groupReferenceValue: this.userSessionService.getLoggedOperator().idDistributor
        }

        return this.gridService.getColumnModelByCategory([
            Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__GRID_PARAMS,
            Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__DEFAULT_GRID_PARAMS
        ],
            LocationService.SIMAX_MODULE_NAME,
            groupReferenceType
        )
    }

    public getMainGridDataSourceParser(): any {
        return (location: OpLocation) => {
            if (location == null) {
                return null
            }

            const additionalFieldsObj: { additionalFields: { key: string, value: any }[] } = {
                additionalFields: []
            }

            // LAT LONG COLS
            if (location.dynamicValues != null) {
                const view: OpView = this.userSessionService.getDataProvider()
                    .View
                    .getActiveViewLocal(LocationService.REFERENCE_TYPE)

                const coords: [number, number] = this.mapService.getCoords(location, { view: view })

                if (coords != null) {
                    const [latitude, longitude]: number[] = coords

                    additionalFieldsObj.additionalFields.push({
                        key: MapHelper.LatitudeFieldName,
                        value: latitude
                    })

                    additionalFieldsObj.additionalFields.push({
                        key: MapHelper.LongitudeFieldName,
                        value: longitude
                    })
                }
            }

            // MARKER TOOLTIP
            const markerFormat: string = ObjectCustomString.getCutomMarkerStringFormat(location)

            if (markerFormat != null) {
                additionalFieldsObj.additionalFields.push({
                    key: MapHelper.MarkerStringFieldName,
                    value: ObjectCustomString.getObjectCustomString(markerFormat, location)
                })
            }

            if (location.dataList != null && location.dataList.length > 0) {

                // MARKEK COLOR
                additionalFieldsObj.additionalFields.push({
                    key: MapHelper.MarkerColorFieldName,
                    value: location.dataList.getValue(Enums.DataType.HELPER_COLOR)
                })

                // CELL COLOR
                additionalFieldsObj.additionalFields.push({
                    key: GridService.CELL_COLOR_FIELD_NAME,
                    value: location.dataList.getValue(Enums.DataType.HELPER_CELL_COLOR)
                })

                // ROW COLOR
                additionalFieldsObj.additionalFields.push({
                    key: GridService.ROW_COLOR_FIELD_NAME,
                    value: location.dataList.getValue(Enums.DataType.HELPER_ROW_COLOR)
                })

            }

            return additionalFieldsObj
        }
    }

    // #endregion

    // #region Options

    public getCreateEditBulkOptions(
        columns: any,
        dataTypeGroupReferenceValue: number,
        items: any[]
    ): BulkEditInitParams {
        return {
            gridColumns: columns,
            groupReferenceType: dataTypeGroupReferenceValue == null ? null : Enums.ReferenceType.IdDistributor,
            groupReferenceValue: dataTypeGroupReferenceValue,
            idReferenceType: LocationService.REFERENCE_TYPE,
            moduleName: LocationService.SIMAX_MODULE_NAME,
            section: null,
            selectedGridRows: TypeHelper.clone(items),
            useBusinessObjects: true
        }
    }

    public getDetailsOptions(idLocation: number, pageItemsIds: number[] = null): DetailsOptions {
        return {
            moduleName: LocationService.SIMAX_MODULE_NAME,
            pageItemsIds: pageItemsIds,
            referenceObjectId: idLocation,
        }
    }

    // #endregion

    // #region Patterns

    public getAvailableParentLocations(view: OpView, columns: ColumnInfo[], pageNbr: number, pageSize: number, customWhereClause: string): JQueryPromise<{ objects: any[]; totalCount: number; error: { code: any; msg: string; }; }> {
        let deletedLocationsConstraint: string = null

        if (view != null) {
            const opts: GetViewColumnOptions = new GetViewColumnOptions(Enums.DataType.HELPER_ID_LOCATION_STATE_TYPE)
            opts.keyColumn = view.getPrimaryKeyColumn()

            const locationStateTypeColumn: OpViewColumn = view.getViewColumn(opts)
            if (locationStateTypeColumn != null) {
                deletedLocationsConstraint = `[${locationStateTypeColumn.name}] != ${Enums.LocationStateType.DELETED}`
            }
        }

        customWhereClause = (customWhereClause || '').trim()

        if (!TypeHelper.isNullOrEmpty(deletedLocationsConstraint)) {
            if (TypeHelper.isNullOrEmpty(customWhereClause)) {
                customWhereClause = deletedLocationsConstraint
            } else {
                customWhereClause = `(${customWhereClause}) AND (${deletedLocationsConstraint})`
            }
        }

        return this.userSessionService.getDataProvider().GroupingLocation.getGroupingLocationFilter(columns, pageNbr, pageSize, customWhereClause)
    }

    public getLocationPatterns(): JQueryPromise<OpLocation[]> {
        return this.userSessionService.getDataProvider()
            .View
            .getActiveView(Enums.ReferenceType.IdLocation)
            .then((view: OpView) => {
                if (view != null) {
                    const idLocationViewColumn: OpViewColumn = view.getPrimaryKeyColumn()

                    const vcOptions: GetViewColumnOptions = new GetViewColumnOptions(Enums.DataType.HELPER_ID_LOCATION_PATTERN)
                    vcOptions.keyColumn = idLocationViewColumn
                    vcOptions.viewColumnType = Enums.ViewColumnType.BaseHelper

                    const idLocationPatternViewColumn: OpViewColumn = view.getViewColumn(vcOptions)

                    if (idLocationViewColumn != null && idLocationPatternViewColumn != null) {
                        const customWhere: string = `([${idLocationViewColumn.name}] = [${idLocationPatternViewColumn.name}])`

                        const columns: ColumnInfo[] = view.columns.map((column: OpViewColumn) => {
                            const columnInfo: ColumnInfo = new ColumnInfo(null)
                            columnInfo.IdDataType = column.idDataType
                            columnInfo.Name = column.name

                            return columnInfo
                        })

                        return this.userSessionService.getDataProvider()
                            .Location
                            .getLocationFilter(columns, 1, TypeHelper.MAX_INT_VALUE, customWhere)
                            .then((result: any) => result.objects || [])
                    }
                }

                return []
            })
            .fail((error: any) => {
                LogHelper.error(LocationService.LOG_PREFIX, error);
            })
    }

    //#endregion

    // #region Parameters

    public getBasicParametersDataTypeGroup(location: OpLocation): JQueryPromise<OpDataTypeInGroup[]> {
        return DataTypeGroupComponent.getDataTypeInGroupByModule(
            this.userSessionService.getDataProvider(),
            Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__PANEL_PARAMS_TO_DISPLAY,
            LocationService.SIMAX_MODULE_NAME,
            Enums.ReferenceType.IdDistributor,
            location.idDistributor
        )
    }

    //#endregion

    // #region Save

    public validateLocation(location: OpLocation): JQueryPromise<OpValidateInfo[]> {
        return this.userSessionService
            .getDataProvider()
            .Location
            .validateLocation([location])
    }

    public saveLocation(location: OpLocation, moduleName: Enums.SimaxModuleName, isBulkSave: boolean = false): JQueryPromise<OpLocation> {
        const deferred: JQueryDeferred<OpLocation> = $.Deferred<OpLocation>()

        this.userSessionService
            .getDataProvider()
            .Location
            .saveLocation(location)
            .then((savedObject: OpLocation) => {
                if (!isBulkSave) {
                    ToastHelper.showSuccess(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVED, moduleName))
                    CommonHelper.setValueToLocalStorage(CommonHelper.localStorageVarForSavedEntity.locationSaved, true, 0)
                }

                deferred.resolve(savedObject)
            })
            .fail((error: any) => {
                if (!isBulkSave) {
                    const errorMsg: string = ConvertHelper.getRequestErrorMessage(error)
                    ToastHelper.showError(errorMsg || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
                }

                deferred.reject(error)
            })

        return deferred.promise()
    }

    // #endregion

    // #region Backward copatibility

    public locationFromLocationFormValuesModel(locationModel: LocationFormValuesModel): OpLocation {
        const location: OpLocation = this.locationFromFormValuesModel(locationModel)

        location.currentDevices.push(...this.getStateList(OpDevice, locationModel.devices, locationModel.addedDevices, locationModel.removedDevices))
        location.currentMeters.push(...this.getStateList(OpMeter, locationModel.meters, locationModel.addedMeters, locationModel.removedMeters))
        location.parentGroups.push(...this.getStateList(OpLocation, locationModel.groups, locationModel.addedGroups, locationModel.removedGroups))

        // Removing

        if (locationModel.locationToAssign != null) {
            if (locationModel.removeWithDevices == null) {
                location.currentDevices
                    .filter((device: OpDevice) => device.opState === Enums.OpChangeState.Delete)
                    .forEach((device: OpDevice) => {
                        const location: OpLocation = new OpLocation(null)

                        location.idObject = locationModel.locationToAssign
                        location.opState = Enums.OpChangeState.Loaded

                        device.currentLocations.push(location)
                    })

                if (locationModel.removeWithMeters === true) {
                    location.currentMeters
                        .filter((meter: OpMeter) => meter.opState === Enums.OpChangeState.Delete)
                        .forEach((meter: OpMeter) => {
                            const location: OpLocation = new OpLocation(null)

                            location.idObject = locationModel.locationToAssign
                            location.opState = Enums.OpChangeState.Loaded

                            meter.opState = Enums.OpChangeState.Loaded

                            meter.currentLocations.push(location)
                        })
                }
            }

            if (locationModel.removeWithMeters == null) {
                location.currentMeters
                    .filter((meter: OpMeter) => meter.opState === Enums.OpChangeState.Delete)
                    .forEach((meter: OpMeter) => {
                        const location: OpLocation = new OpLocation(null)

                        location.idObject = locationModel.locationToAssign
                        location.opState = Enums.OpChangeState.Loaded

                        meter.currentLocations.push(location)
                    })

                if (locationModel.removeWithDevices === true) {
                    location.currentDevices
                        .filter((device: OpDevice) => device.opState === Enums.OpChangeState.Delete)
                        .forEach((device: OpDevice) => {
                            const location: OpLocation = new OpLocation(null)

                            location.idObject = locationModel.locationToAssign
                            location.opState = Enums.OpChangeState.Loaded

                            device.opState = Enums.OpChangeState.Loaded

                            device.currentLocations.push(location)
                        })
                }
            }
        }

        // Removing End


        // Adding

        // Adding new device and meter
        if (locationModel.deviceWithMeter && locationModel.deviceWithMeter.length > 0) {
            locationModel.deviceWithMeter.forEach((model: DeviceMeterModel) => {
                const existingDevice: OpDevice = _.find(location.currentDevices, (device: OpDevice) => device.idObject === model.serialNbr)

                if (existingDevice != null) {
                    const meter: OpMeter = new OpMeter(null)

                    meter.idObject = model.idMeter
                    meter.opState = Enums.OpChangeState.Loaded

                    existingDevice.currentMeters.push(meter)
                    existingDevice.opState = Enums.OpChangeState.New

                    const device: OpDevice = new OpDevice(null)

                    device.idObject = existingDevice.idObject
                    device.opState = Enums.OpChangeState.New

                    meter.currentDevices.push(device)
                    location.currentMeters.push(meter)
                } else {
                    const existingMeter: OpMeter = _.find(location.currentMeters, (meter: OpMeter) => meter.idObject === model.idMeter)

                    if (existingMeter != null) {
                        const device: OpDevice = new OpDevice(null)

                        device.idObject = model.serialNbr
                        device.opState = Enums.OpChangeState.Loaded

                        existingMeter.currentDevices.push(device)
                        existingMeter.opState = Enums.OpChangeState.New

                        const meter: OpMeter = new OpMeter(null)

                        meter.idObject = model.idMeter
                        meter.opState = Enums.OpChangeState.New

                        device.currentMeters.push(meter)
                        location.currentDevices.push(device)
                    }
                }
            })
        }

        // Adding a meter to an existing device or a device to an existing meter
        if (locationModel.deviceMeter && locationModel.deviceMeter.length > 0) {
            locationModel.deviceMeter.forEach((model: DeviceMeterModel) => {
                const existingDevice: OpDevice = _.find(location.currentDevices, (device: OpDevice) => device.idObject === model.serialNbr)

                if (existingDevice != null) {
                    let meter: OpMeter = _.find(existingDevice.currentMeters, (meter: OpMeter) => meter.idObject === model.idMeter)
                    let meterExists: boolean = true

                    if (meter == null) {
                        meter = new OpMeter(null)
                        meter.idObject = model.idMeter

                        meterExists = false
                    }

                    meter.opState = Enums.OpChangeState.New

                    if (!meterExists) {
                        existingDevice.currentMeters.push(meter)
                    }

                    existingDevice.opState = Enums.OpChangeState.New

                    const device: OpDevice = new OpDevice(null)

                    device.idObject = existingDevice.idObject
                    device.opState = Enums.OpChangeState.New

                    if (!meterExists) {
                        meter.currentDevices.push(device)
                        location.currentMeters.push(meter)
                    }
                } else {
                    const existingMeter: OpMeter = _.find(location.currentMeters, (meter: OpMeter) => meter.idObject === model.idMeter)

                    if (existingMeter != null) {
                        let device: OpDevice = _.find(existingMeter.currentDevices, (device: OpDevice) => device.idObject === model.serialNbr)
                        let deviceExists = true

                        if (device == null) {
                            device = new OpDevice(null)
                            device.idObject = model.serialNbr

                            deviceExists = false
                        }

                        device.opState = Enums.OpChangeState.New

                        if (!deviceExists) {
                            existingMeter.currentDevices.push(device)
                        }

                        existingMeter.opState = Enums.OpChangeState.New

                        const meter: OpMeter = new OpMeter(null)

                        meter.idObject = existingMeter.idObject
                        meter.opState = Enums.OpChangeState.New

                        if (!deviceExists) {
                            device.currentMeters.push(meter)
                            location.currentDevices.push(device)
                        }
                    }
                }
            })
        }

        // Adding End


        return location
    }

    public validateLocationsChunked<TModel>(
        formModels: TModel[],
        modelMapFunction: (model: TModel) => OpLocation
    ): JQueryPromise<OpValidateInfo[]> {

        const pageSize: number = ConfigHelper.getSectionValue(Enums.SimaxConfigSection.LocationValidationPageSize, { defaultValue: 100 })

        const deferred: JQueryDeferred<OpValidateInfo[]> = $.Deferred<OpValidateInfo[]>()
        const currentValidateInfo: OpValidateInfo[] = []

        const innerLoop = (iteration: number) => {
            const start: number = iteration * pageSize
            const end: number = start + pageSize
            const locations: OpLocation[] = formModels
                .slice(start, end)
                .map(modelMapFunction)

            this.userSessionService.getDataProvider().Location
                .validateLocation(locations)
                .then((validateInfo: OpValidateInfo[]) => {
                    if (validateInfo != null && validateInfo.length > 0) {
                        currentValidateInfo.push(...validateInfo)

                        if (formModels.length > end) {
                            innerLoop(++iteration)
                        } else {
                            deferred.resolve(currentValidateInfo)
                        }
                    } else {
                        deferred.reject("No response or response empty.")
                    }
                })
                .fail((error: any) => {
                    deferred.reject(error)
                })
        }

        innerLoop(0)

        return deferred
            .fail((error) => {
                console.warn(LocationService.LOG_PREFIX, "Unable to validate chunked locations.", error)

                return $.Deferred<OpValidateInfo[]>().reject()
            })
            .promise()
    }

    private getStateList<T extends IOpObject>(ctor: new (opObj: any) => T, loadedIds: number[], newIds: number[], deletedIds: number[]): T[] {
        const objects: T[] = []

        if (_.size(loadedIds) > 0) {
            const loadedObjects: T[] = loadedIds.map((id: number) => {
                const object: T = new ctor(null)

                object.idObject = id
                object.opState = Enums.OpChangeState.Loaded

                return object
            })

            objects.push(...loadedObjects)
        }

        if (_.size(newIds) > 0) {
            const newObjects: T[] = newIds.map((id: number) => {
                const object: T = new ctor(null)

                object.idObject = id
                object.opState = Enums.OpChangeState.New

                return object
            })

            objects.push(...newObjects)
        }

        if (_.size(deletedIds) > 0) {
            const deletedObjects: T[] = deletedIds.map((id: number) => {
                const object: T = new ctor(null)

                object.idObject = id
                object.opState = Enums.OpChangeState.Delete

                return object
            })

            objects.push(...deletedObjects)
        }

        return objects
    }

    private locationFromFormValuesModel(model: FormValuesModel): OpLocation {
        const location: OpLocation = new OpLocation(null)

        location.idObject = model.id || 0

        if (model.fields != null) {
            const locationData: OpData[] = model.fields.map((field: FieldNameValueModel) => {
                const data: OpData = new OpData(null)

                data.idData = field.id
                data.idDataType = field.idDataType
                data.opState = field.state
                data.indexNbr = field.index
                data.value = field.value

                return data
            })
            location.dataList.push(...locationData)
        }

        if (model.id > 0) {
            location.opState = Enums.OpChangeState.Modified
        }

        return location
    }

    // #endregion

    // #region ContextList    

    public getContextListReferenceObjects(locations: OpLocation[]): Dictionary<OpLocation[]> {
        return Dictionary.createFromGroup(locations, KeyType.Number, (opLocation: OpLocation) => this.getModuleName(opLocation))
    }

    public getWhereToRedirect(referenceObjects: Dictionary<IReferenceType[]>): { id: number, name: string }[] {
        const redirectToSelect: { id: number, name: string }[] = []
        referenceObjects.keys().forEach((moduleId: number) => {
            if (this.hasPermissionToRedirect(moduleId)) {
                switch (moduleId) {
                    case Enums.SimaxModuleName.Location:
                        redirectToSelect.unshift({ id: moduleId, name: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__NAME, moduleId) })
                        break

                    case Enums.SimaxModuleName.Depository:
                        redirectToSelect.push({ id: moduleId, name: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__NAME, moduleId) })
                        break

                    default:
                        LogHelper.error(LocationService.LOG_PREFIX, `Redirection to module: ${moduleId} isn't served`)
                }
            }
        })
        return redirectToSelect
    }

    private getModuleName(location: OpLocation): Enums.SimaxModuleName {
        if (location.idLocationType === Enums.LocationType.DepositoryLocation) {
            return Enums.SimaxModuleName.Depository
        }

        return Enums.SimaxModuleName.Location
    }

    private hasPermissionToRedirect(moduleName: Enums.SimaxModuleName): boolean {
        if (moduleName === Enums.SimaxModuleName.Depository) {
            return this.userSessionService.getLoggedOperator().hasPermission(Activity.DEPOSITORY_LIST)
        } else if (moduleName === Enums.SimaxModuleName.Location) {
            return this.userSessionService.getLoggedOperator().hasPermission(Activity.LOCATION_LIST)
        }

        return false
    }

    //#endregion

    // #region Delivery

    public planDelivery(idLocations: number[], deliveryDateTime: Date): DataProviderRequest<void> {
        const momentDate: moment.Moment = moment(TypeHelper.stripHours(deliveryDateTime))
        const deliveryDate: Date = momentDate.add(momentDate.utcOffset(), 'minutes').toDate()
        const request = this.userSessionService
            .getDataProvider().Location
            .locationPlanDeliveryTime(idLocations, deliveryDate)

        request.catch((error: DataProviderRequestError) => {
            LogHelper.warn(LocationService.LOG_PREFIX, `Unable to plan delivery for locations: ${idLocations} and date: ${deliveryDateTime}`, error.toString())
        })

        return request
    }

    public clearDelivery(idLocations: number[]): DataProviderRequest<void> {
        const request = this.userSessionService
            .getDataProvider().Location
            .locationPlanDeliveryTime(idLocations, null)

        request.catch((error: DataProviderRequestError) => {
            LogHelper.warn(LocationService.LOG_PREFIX, `Unable to clear planned delivery for locations: ${idLocations}.`, error.toString())
        })

        return request
    }

    // #endregion

    // #region Problems

    public confirmLocationProblems(idLocations: ReadonlyArray<number>, problemClass: ReadonlyArray<Enums.ProblemClass>): DataProviderRequest<void> {
        const request = this.userSessionService
            .getDataProvider()
            .Location
            .confirmLocationProblem(idLocations, problemClass)

        request.catch((error: DataProviderRequestError) => {
            LogHelper.warn(LocationService.LOG_PREFIX, `Unable to confirm problems for locations: ${idLocations}`, error.toString())
        })

        return request
    }

    public getLocationProblems(idLocations: ReadonlyArray<number>): DataProviderRequest<OpProblemClass[]> {
        const request = this.userSessionService
            .getDataProvider()
            .Location
            .getLocationProblemClass(idLocations)

        request.catch((error: DataProviderRequestError) => {
            LogHelper.warn(LocationService.LOG_PREFIX, `Unable to get problems for locations: ${idLocations}`, error.toString())
        })

        return request
    }

    // #endregion
}

export class LocationServiceProvider
    implements ng.IServiceProvider {
    // #region IServiceProvider Implementation 

    public $get = [
        USER_SESSION_SERVICE_NAME,
        GRID_SERVICE_NAME,
        REFERENCE_TYPE_SERVICE_NAME,
        SYSTEM_SERVICE_NAME,
        MAP_SERVICE_NAME,
        GROUPING_LOCATION_SERVICE_NAME,
        function (
            userSession: UserSessionService,
            gridService: GridService,
            referenceTypeService: ReferenceTypeService,
            systemService: SystemService,
            mapService: MapService,
            groupingLocationService: GroupingLocationService
        ) {
            return new LocationService(userSession, gridService, referenceTypeService, systemService, mapService, groupingLocationService)
        }
    ]

    // #endregion
} 
